if (!customElements.get('subscription-form')) {
  /**
   * A custom HTML element representing a subscription form.
   *
   * @class SubscriptionForm
   * @extends HTMLElement
   *
   * @constructor
   * Initializes the subscription form element.
   *
   * @method connectedCallback
   * Sets up event listeners for form submission and input changes,
   * initializes form elements, and updates the price after a delay.
   *
   * @method onSubmit
   * Handles the form submission, prevents default behavior,
   * collects form data, and interacts with the Cart API to add items.
   *
   * @method onInputChange
   * Updates the displayed price based on input changes in the form.
   *
   * @method updatePrice
   * Fetches the updated price for the selected product variant
   * and updates the price display in the DOM.
   *
   * @method getSellingPlanIdFromFormData
   * Extracts the selling plan ID from the form data based on
   * the selected product and variant.
   *
   * @method handleErrorMessage
   * Displays an error message in the error container if present,
   * or hides it if the message is empty.
   */
  class SubscriptionForm extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.addEventListener('submit', (event) => this.onSubmit(event));
      this.submitButton = this.querySelector('button[type="submit"]');
      this.cartDrawer = document.querySelector('cart-drawer');
      this.form = this.querySelector('form');
      this.errorContainer = this.querySelector('.error-message');
      this.sellingPlans = JSON.parse(
        document.querySelector('[data-selling-plans-json][type="application/json"]').textContent,
      );

      [...this.form.elements].forEach((element) => {
        element.addEventListener('change', this.onInputChange.bind(this));
      });

      setTimeout(() => {
        this.updatePrice();
      }, 300);
    }

    /**
     * Handles the form submission event.
     *
     * @param {Event} event - The submit event triggered by the form.
     * @returns {void}
     *
     * @throws {Error} Throws an error if the cart operation fails.
     *
     * This method prevents the default form submission, collects form data,
     * appends necessary sections and selling plan ID, and then adds the
     * items to the cart. It handles the response by rendering the cart
     * contents or displaying an error message if the operation fails.
     */
    onSubmit(event) {
      event.preventDefault();
      this.submitButton.classList.add('disabled');

      const formData = new FormData(this.form);
      const sections = this.cartDrawer.getSectionsToRender().map((section) => section.section);
      formData.append('sections', sections);
      formData.append('sections_url', window.location.pathname);

      const sellingPlanId = this.getSellingPlanIdFromFormData(formData);
      if (sellingPlanId) {
        formData.append('selling_plan', sellingPlanId);
      }

      Cart.add(formData)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            this.handleErrorMessage(response.description);
            return;
          }
          this.cartDrawer.renderContents(response);
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          this.submitButton.classList.remove('disabled');
        });
    }

    /**
     * Handles the input change event.
     * This method is called whenever there is a change in the input field,
     * and it updates the price accordingly.
     */
    onInputChange() {
      this.updatePrice();
    }

    /**
     * Updates the price displayed on the subscription form based on the selected variant and selling plan.
     *
     * This method retrieves the form data, constructs a URL with the necessary parameters,
     * and fetches the updated price from the server. If the new price is found in the response,
     * it updates the price container in the DOM.
     *
     * @this {HTMLElement} The element that the method is called on, which should have a data-product-url attribute.
     * @returns {void} This method does not return a value.
     */
    updatePrice() {
      const formData = new FormData(this.form);
      const productUrl = this.getAttribute('data-product-url');
      const url = new URL(productUrl, window.location.origin);

      url.searchParams.append('section_id', 'main-product');
      url.searchParams.append('variant', formData.get('id'));
      url.searchParams.append('selling_plan', this.getSellingPlanIdFromFormData(formData));

      const fetchURL = url.toString();

      fetch(fetchURL)
        .then((response) => response.text())
        .then((html) => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          const newContent = doc.querySelector('.price');

          if (newContent) {
            const priceContainer = document.querySelector('.subscription-flow__price');
            priceContainer.innerHTML = newContent.innerHTML;
          }
        });
    }

    /**
     * Retrieves the selling plan ID from the provided form data and the selling plans JSON.
     *
     * @param {FormData} formData - The form data containing product and variant information.
     * @returns {string|null} The selling plan ID if found, otherwise null.
     */
    getSellingPlanIdFromFormData(formData) {
      const productId = formData.get('product-id');
      const variantId = formData.get('id');
      const sellingPlanGroup =
        formData.get('plan') === 'prepaid' ? formData.get('prepaid-duration') : 'ongoing';
      const frequency = formData.get('frequency');

      if (this.sellingPlans) {
        return this.sellingPlans[productId].variants[variantId].selling_plan_groups[
          sellingPlanGroup
        ].frequency[frequency];
      }

      return null;
    }

    /**
     * Handles the display of error messages in the error container.
     *
     * @param {string} message - The error message to be displayed.
     * If the message is an empty string, the error container will be hidden.
     */
    handleErrorMessage(message) {
      this.errorContainer.textContent = message;
      if (message !== '') {
        this.errorContainer.classList.remove('hidden');
        return;
      }
      this.errorContainer.classList.add('hidden');
    }
  }
  customElements.define('subscription-form', SubscriptionForm);
}
